import { formatPeriodLong } from '@commissionsight/shared';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../api.js';
import { PageTabs, chargebacksTabs } from '../components/PageTabs.js';
import { PeriodPicker } from '../components/PeriodPicker.js';
import { WorkspaceFilter } from '../components/WorkspaceFilter.js';
import { Button, Card, EmptyState, Field, ICONS, Loading, inputClass } from '../components/ui.js';
import { formatCurrency } from '../lib/status.js';
import { useCarriers } from '../lib/useCarriers.js';

type GapStatus = 'unpaid' | 'partial' | 'caught_up';

interface GapRow {
  gapStart: string;
  gapEnd: string;
  months: number;
  memberRefId: string;
  policyRefId: string;
  memberExternalId: string | null;
  memberName: string | null;
  policyNumber: string | null;
  planName: string | null;
  carrierId: string;
  carrierName: string | null;
  paidThrough: string;
  rateBefore: number;
  resumedPeriod: string;
  resumedAmount: number;
  status: GapStatus;
  estUnpaid: number;
  estAtPriorRate: number;
}

interface GapSummary {
  runs: number;
  gapMonths: number;
  policies: number;
  unpaidRuns: number;
  partialRuns: number;
  caughtUpRuns: number;
  estUnpaid: number;
  estAtPriorRate: number;
  estUnpaidFlatOnly: number;
  from: string | null;
  to: string | null;
  truncated: boolean;
}

/** Grid page size — summary tiles and the CSV export always cover the whole range. */
const PAGE_SIZE = 100;

/**
 * Default window: gaps that began in the last ~60 days (two statement months).
 * The full-history scan is a click away, but it walks every policy's entire
 * payment timeline — several seconds on a large book — so it's opt-in.
 */
function defaultFromPeriod(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 2);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const STATUS_BADGE: Record<GapStatus, { label: string; className: string }> = {
  unpaid: {
    label: 'unpaid',
    className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  },
  partial: {
    label: 'partial catch-up',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  caught_up: {
    label: 'caught up',
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
};

/**
 * Payment gaps — the unpaid mirror of the proof-out: months with no record at all
 * between two paid months on the same policy, classified by whether the carrier
 * ever caught the money up, with an estimate and a carrier-ready CSV export.
 * Entitlement-gated (`paymentGaps`).
 */
export function PaymentGaps() {
  const { baseUrl, token, entitlements } = useAuth();
  const { carriers } = useCarriers();
  // Deep links (e.g. from an Ask AI answer) carry the exact filters the numbers
  // were computed with; without params the page opens on the 60-day default.
  const [urlParams] = useSearchParams();
  const defaultFrom = defaultFromPeriod();
  const [carrierId, setCarrierId] = useState(urlParams.get('carrierId') ?? '');
  const [workspaceId, setWorkspaceId] = useState('');
  const [from, setFrom] = useState(
    urlParams.has('from') || urlParams.has('to') ? (urlParams.get('from') ?? '') : defaultFrom,
  );
  const [to, setTo] = useState(urlParams.get('to') ?? '');
  const isDefaultWindow = from === defaultFrom && !to;
  const isFullHistory = !from && !to;
  const [summary, setSummary] = useState<GapSummary | null>(null);
  const [rows, setRows] = useState<GapRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // Grid pagination — the summary tiles and the CSV export ALWAYS cover the whole
  // range; only the table pages.
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  // Filter query string, shared by the JSON load and the CSV export. The CSV url
  // deliberately carries NO limit/offset — the export is always the entire list.
  const params = new URLSearchParams();
  if (carrierId) params.set('carrierId', carrierId);
  if (workspaceId) params.set('workspaceId', workspaceId);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  // Empty pickers = explicit full-history request → tell the API, or it would
  // re-apply its own 3-month default window.
  if (!from && !to) params.set('range', 'all');
  const qs = params.toString();
  const gapsUrl = `${baseUrl.replace(/\/$/, '')}/chargebacks/gaps?${qs ? `${qs}&` : ''}limit=${PAGE_SIZE}&offset=${offset}`;
  const csvUrl = `${baseUrl.replace(/\/$/, '')}/chargebacks/gaps?${qs ? `${qs}&` : ''}format=csv`;

  // Changing any filter resets to the first page.
  const changeCarrier = (v: string) => {
    setCarrierId(v);
    setOffset(0);
  };
  const changeWorkspace = (v: string) => {
    setWorkspaceId(v);
    setOffset(0);
  };
  const changeFrom = (v: string) => {
    setFrom(v);
    setOffset(0);
  };
  const changeTo = (v: string) => {
    setTo(v);
    setOffset(0);
  };

  const enabled = entitlements.paymentGaps;
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setRows(null);
    setError(null);
    fetch(gapsUrl, { headers: { authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Failed to load (${r.status})`);
        return r.json();
      })
      .then((b) => {
        if (!active) return;
        setSummary(b.summary);
        setRows(b.data ?? []);
        setTotal(b.pagination?.total ?? (b.data ?? []).length);
      })
      .catch((e) => active && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => {
      active = false;
    };
  }, [enabled, gapsUrl, token]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch(csvUrl, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const match = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = match?.[1] ?? 'payment-gaps.csv';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (!entitlements.paymentGaps) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card>
          <EmptyState
            icon={ICONS.list}
            title="Payment gaps"
            hint="This feature isn't enabled for your account. Contact CommissionSight to turn it on."
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTabs tabs={chargebacksTabs(entitlements)} />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Payment gaps</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Months the carrier paid nothing on a policy it was paying both before and after — with
            no chargeback explaining the silence. Runs the carrier later caught up with a lump-sum
            payment are marked; the rest is commission with no evidence it was ever paid.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <WorkspaceFilter value={workspaceId} onChange={changeWorkspace} />
          <div className="w-44">
            <Field label="Carrier">
              <select
                className={inputClass}
                value={carrierId}
                onChange={(e) => changeCarrier(e.target.value)}
              >
                <option value="">All carriers</option>
                {carriers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="w-44">
            <Field label="From">
              <PeriodPicker value={from} onChange={changeFrom} placeholder="First period" />
            </Field>
          </div>
          <div className="w-44">
            <Field label="To">
              <PeriodPicker value={to} onChange={changeTo} placeholder="Latest period" />
            </Field>
          </div>
          {(from || to) && (
            <Button
              variant="ghost"
              onClick={() => {
                setFrom('');
                setTo('');
                setOffset(0);
              }}
            >
              Clear range
            </Button>
          )}
          <Button onClick={exportCsv} loading={exporting} disabled={!rows || rows.length === 0}>
            Export CSV
          </Button>
        </div>
      </div>

      {error && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}

      {isDefaultWindow && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-neutral-200 bg-neutral-50 px-4 py-2.5 dark:border-neutral-800 dark:bg-neutral-900">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Showing gaps that began in the last 60 days. Your full history holds the complete
            picture — on a large book that scan can take several seconds.
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFrom('');
              setTo('');
              setOffset(0);
            }}
          >
            Scan full history →
          </Button>
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <Card>
            <p className="text-xs text-neutral-500">Missing months</p>
            <p className="mt-1 text-2xl font-semibold">{summary.gapMonths}</p>
            <p className="mt-0.5 text-xs text-neutral-400">
              {summary.from && summary.to
                ? `${formatPeriodLong(summary.from)} – ${formatPeriodLong(summary.to)}`
                : 'no data'}
            </p>
          </Card>
          <Card>
            <p className="text-xs text-neutral-500">Est. unpaid</p>
            <p className="mt-1 text-2xl font-semibold text-rose-600 dark:text-rose-400">
              {formatCurrency(summary.estUnpaid)}
            </p>
            <p className="mt-0.5 text-xs text-neutral-400">
              across {summary.policies} {summary.policies === 1 ? 'policy' : 'policies'}
            </p>
          </Card>
          <Card>
            <p className="text-xs text-neutral-500">Never caught up</p>
            <p className="mt-1 text-2xl font-semibold text-rose-600 dark:text-rose-400">
              {summary.unpaidRuns}
            </p>
            <p className="mt-0.5 text-xs text-neutral-400">
              {formatCurrency(summary.estUnpaidFlatOnly)} with no catch-up evidence
            </p>
          </Card>
          <Card>
            <p className="text-xs text-neutral-500">Caught up by the carrier</p>
            <p className="mt-1 text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
              {summary.caughtUpRuns}
            </p>
            <p className="mt-0.5 text-xs text-neutral-400">
              {summary.partialRuns > 0 ? `+ ${summary.partialRuns} partial` : 'lump-sum true-ups'}
            </p>
          </Card>
          <Card>
            <p className="text-xs text-neutral-500">Upper bound</p>
            <p className="mt-1 text-2xl font-semibold">{formatCurrency(summary.estAtPriorRate)}</p>
            <p className="mt-0.5 text-xs text-neutral-400">at prior rates, before credits</p>
          </Card>
        </div>
      )}

      {summary?.truncated && (
        <p className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
          Showing the first {summary.runs} gaps — narrow the period range or filter by carrier to
          see the rest.
        </p>
      )}

      <Card className="p-0">
        <div className="grid grid-cols-[1.1fr_1.5fr_1fr_0.9fr_1.3fr_1.3fr_0.9fr] gap-4 border-b border-neutral-200 px-5 py-2 text-xs font-medium text-neutral-500 dark:border-neutral-800">
          <span>Missing</span>
          <span>Member / policy</span>
          <span>Carrier</span>
          <span className="text-right">Monthly rate</span>
          <span>Paid through → resumed</span>
          <span className="text-right">Status</span>
          <span className="text-right">Est. unpaid</span>
        </div>
        {!rows ? (
          <Loading
            label={
              isFullHistory
                ? 'Scanning your full history — large books can take several seconds…'
                : 'Scanning for payment gaps…'
            }
          />
        ) : rows.length === 0 ? (
          <p className="py-12 text-center text-sm text-neutral-500">
            {isDefaultWindow
              ? 'No payment gaps began in the last 60 days — scan your full history for the complete picture.'
              : 'No payment gaps in the selected range.'}
          </p>
        ) : (
          <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {rows.map((r, i) => (
              <div
                key={`${r.gapStart}-${r.policyRefId}-${i}`}
                className="grid grid-cols-[1.1fr_1.5fr_1fr_0.9fr_1.3fr_1.3fr_0.9fr] items-center gap-4 px-5 py-3 text-sm"
              >
                <div className="text-xs">
                  <div className="font-medium text-neutral-700 dark:text-neutral-300">
                    {r.months === 1
                      ? formatPeriodLong(r.gapStart)
                      : `${formatPeriodLong(r.gapStart)} – ${formatPeriodLong(r.gapEnd)}`}
                  </div>
                  <div className="text-neutral-400">
                    {r.months} {r.months === 1 ? 'month' : 'months'}
                  </div>
                </div>
                <div className="min-w-0">
                  <Link
                    to={`/members/${r.memberRefId}`}
                    className="truncate font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                    title="View this member's full history"
                  >
                    {r.memberName ?? r.memberExternalId ?? r.memberRefId}
                  </Link>
                  <Link
                    to={`/policies/${r.policyRefId}`}
                    className="block truncate text-xs text-neutral-400 hover:text-emerald-600 hover:underline"
                  >
                    {r.policyNumber ?? '—'}
                    {r.planName ? ` · ${r.planName}` : ''}
                  </Link>
                </div>
                <div className="truncate text-xs text-neutral-500">
                  {r.carrierName ?? r.carrierId}
                </div>
                <div className="text-right text-xs text-neutral-700 dark:text-neutral-300">
                  {formatCurrency(r.rateBefore)}
                  {r.resumedAmount !== r.rateBefore && (
                    <div className="text-neutral-400">→ {formatCurrency(r.resumedAmount)}</div>
                  )}
                </div>
                <div className="text-xs text-neutral-500">
                  {formatPeriodLong(r.paidThrough)} → {formatPeriodLong(r.resumedPeriod)}
                </div>
                <div className="text-right">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[r.status].className}`}
                  >
                    {STATUS_BADGE[r.status].label}
                  </span>
                </div>
                <div
                  className={`text-right font-semibold ${
                    r.estUnpaid > 0
                      ? 'text-rose-600 dark:text-rose-400'
                      : 'text-neutral-400 dark:text-neutral-500'
                  }`}
                >
                  {formatCurrency(r.estUnpaid)}
                </div>
              </div>
            ))}
          </div>
        )}
        {rows && total > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-neutral-200 px-5 py-3 dark:border-neutral-800">
            <span className="text-xs text-neutral-500">
              Showing {total === 0 ? 0 : offset + 1}–{offset + rows.length} of {total} gaps — the
              CSV export always includes all {total}.
            </span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                ← Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={offset + rows.length >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next →
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
